import _toConsumableArray from "@babel/runtime/helpers/esm/toConsumableArray";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
import { createSelector } from 'reselect';
import { CART_STATE_KEY, CART_STATE, FULL_CUSTOMIZATION, INITIAL_STATE, JERSEY_ID, META_STATE, TOTALS_STATE, WARRANTY } from '@nike/cart-constants';
import { NIKE_BRAND, NIKECOM_CHANNEL } from '@nike/ux-tread-dotcom-constants';
var propOr = function propOr(prop, or) {
  return function (obj) {
    return obj[prop] || or;
  };
};
export var selectCarts = function selectCarts(state) {
  return propOr(CART_STATE_KEY, INITIAL_STATE)(state);
};

/**
 * Retrieves the cart object
 *
 * @param {object} state - redux state that contains @carts state
 * @returns {object}     Object that contains the user state
 */
export var getCart = createSelector(selectCarts, propOr('cart', CART_STATE));

/**
 * getCartErrors retrieves the carts error substate
 *
 * @param {object} state -  redux state that contains @carts state
 * @returns {Array}       errors array
 */
export var getCartErrors = createSelector(selectCarts, propOr('errors', []));

/**
 * getCartsLocale retrieves the carts locale substate
 *
 * @param {object} state -  redux state that contains @carts state
 * @returns {object}      locale object
 */
export var getCartsLocale = createSelector(selectCarts, propOr('locale', {}));

/**
 * getCartDataErrors retrieves cart.errors from cart substate
 *
 * @param {object} state -      redux state that contains @carts state
 * @returns {Array|undefined} errors array or undefined
 */
export var getCartDataErrors = createSelector(getCart, function (cart) {
  return cart.errors || [];
});

/**
 * getCartDataErrors retrieves cart.errors from cart substate
 *
 * @param {object} state -    redux state that contains @carts state
 * @returns {boolean}       boolean indicating if cart data has errors
 */
export var getCartDataHasErrors = createSelector(getCartDataErrors, function (errors) {
  return Boolean(errors.length);
});

/**
 * getCartWarnings retrieves cart.warnings from cart substate
 *
 * @param {object} state -  redux state that contains @carts state
 * @returns {Array}       warnings array
 */
export var getCartWarnings = createSelector(getCart, function (cart) {
  return cart.warnings || [];
});

/**
 * getCartHasWarnings retrieves a boolean check against cart.warnings
 *
 * @param {object} state -    redux state that contains @carts state
 * @returns {boolean}       boolean indicating if cart data has warnings
 */
export var getCartHasWarnings = createSelector(getCartWarnings, function (warnings) {
  return Boolean(warnings.length);
});

/**
 * Retrieves the brand of the user's experiences
 *
 * @param {object} state - The cart object
 * @returns {string}
 */
export var getBrand = createSelector(getCart, propOr('brand', NIKE_BRAND));

/**
 * Retrieves the channel of the user's experience
 *
 * @param {object} state - The cart object
 * @returns {string}
 */
export var getChannel = createSelector(getCart, propOr('channel', NIKECOM_CHANNEL));

/**
 * Retrieves the country of the user's experience
 *
 * @param {object} state - The cart object
 * @returns {string}
 */
export var getCountry = createSelector(getCart, function (_ref) {
  var country = _ref.country;
  return country;
});

/**
 * Retrieves the currency of the user's experience
 * NOTE: we can't use these initially because when we make the first call this data isn't available to us.. same for country, same for language,
 *
 * @param {object} state - The cart object
 * @returns {string}
 */
export var getCurrency = createSelector(getCart, function (_ref2) {
  var currency = _ref2.currency;
  return currency;
});

/**
 * Retrieves the cart items
 *
 * @param {object} state - The cart object
 * @returns {Array}
 */
export var getCartItems = createSelector(getCart, propOr('items', []));
export var getCartsItemsObject = createSelector(getCartItems, function (itemsArray) {
  return itemsArray.reduce(function (itemsObj, currentItem) {
    return _objectSpread(_defineProperty({}, currentItem.id, currentItem), itemsObj);
  }, {});
});
export var getCartItemById = function getCartItemById(state) {
  return function (id) {
    return getCartsItemsObject(state)[id] || null;
  };
};

/**
 * @param {object} state - The cart object
 * @returns {Array}
 */
export var getCartItemIds = createSelector(selectCarts, propOr('itemIds', []));

/**
 * Retrieve unique product type count
 *
 * @param {object} state - The cart object
 * @returns {Array}
 */
export var getUniqueProductCount = createSelector(getCartItems, function (items) {
  return items && items.length || 0;
});

/**
 * Retrieves the cart id
 *
 * @param {object} state - The cart object
 * @returns {string}
 */
export var getCartId = createSelector(getCart, propOr('id', ''));

/**
 * Creates an object of VAS instruction type keys and all cart items that have that VAS instruction applied.
 *
 * @param {object} state - The cart object
 * @returns {object}
 */
export var getCartItemIdsByVASInstructionTypes = createSelector(getCartItems, function (cartItems) {
  return cartItems.reduce(function (accumulator, currentItem) {
    var vas = currentItem.valueAddedServices || [];
    vas.forEach(function (vasItem) {
      var _vasItem$instruction;
      var currentType = (vasItem === null || vasItem === void 0 ? void 0 : (_vasItem$instruction = vasItem.instruction) === null || _vasItem$instruction === void 0 ? void 0 : _vasItem$instruction.type) || '';
      if (currentType) {
        accumulator[currentType] = accumulator[currentType] ? [].concat(_toConsumableArray(accumulator[currentType]), [currentItem.id]) : [currentItem.id];
      }
    });
    return accumulator;
  }, {});
});

/**
 * Indicates if cart has any items with WARRANTY are applied (like Applecare).
 *
 * @param {object} state - The cart object
 * @returns {boolean} Boolean indicating if cart has WARRANTY applied
 */
export var getCartHasWarrantyItems = createSelector(getCartItemIdsByVASInstructionTypes, function (cartItemIdsByVASInstructionTypes) {
  return cartItemIdsByVASInstructionTypes[WARRANTY] && cartItemIdsByVASInstructionTypes[WARRANTY].length > 0 || false;
});

/**
 * Indicates if cart has any items with FULL_CUSTOMIZATION are applied (like NBY/NIKEiD).
 *
 * @param {object} state - The cart object
 * @returns {boolean} Boolean indicating if cart has FULL_CUSTOMIZATION applied
 */
export var getCartHasNikeIdItems = createSelector(getCartItemIdsByVASInstructionTypes, function (cartItemIdsByVASInstructionTypes) {
  return cartItemIdsByVASInstructionTypes[FULL_CUSTOMIZATION] && cartItemIdsByVASInstructionTypes[FULL_CUSTOMIZATION].length > 0 || false;
});

/**
 * Indicates if cart has any items with JERSEY_ID are applied.
 *
 * @param {object} state - The cart object
 * @returns {boolean} Boolean indicating if cart has JERSEY_ID applied
 */
export var getCartHasJerseyIdItems = createSelector(getCartItemIdsByVASInstructionTypes, function (cartItemIdsByVASInstructionTypes) {
  return cartItemIdsByVASInstructionTypes[JERSEY_ID] && cartItemIdsByVASInstructionTypes[JERSEY_ID].length > 0 || false;
});

/**
 * Retrieves all NikeID items from cart items.
 *
 * @returns {Array}
 */
export var getNikeIdItems = createSelector(getCartItems, function (cartItems) {
  return cartItems.filter(function (item) {
    var vas = item.valueAddedServices || [];
    return vas.filter(function (vasItem) {
      return vasItem.instruction && vasItem.instruction.type === FULL_CUSTOMIZATION;
    }).length > 0;
  });
});

/**
 * Retrieves all JerseyId items from cart items.
 *
 * @returns {Array}
 */
export var getJerseyIdItems = createSelector(getCartItems, function (cartItems) {
  return cartItems.filter(function (item) {
    var vas = item.valueAddedServices || [];
    return vas.filter(function (vasItem) {
      return vasItem.instruction && vasItem.instruction.type === JERSEY_ID;
    }).length > 0;
  });
});

/**
 * Retrieves all NikeId designViewIds from cart items.
 *
 * @returns {Array}
 */
// TODO: MYBAG-1650 refactor to not use first value
export var getNikeIdDesignViewIds = createSelector(getNikeIdItems, function (nikeIdItems) {
  return nikeIdItems.filter(function (nikeIdItem) {
    return nikeIdItem.valueAddedServices[0].instruction.type === FULL_CUSTOMIZATION;
  }).map(function (item) {
    return item.valueAddedServices[0].instruction.id;
  });
});

/**
 * Retrieves all JerseyId designViewIds from cart items.
 *
 * @returns {Array}
 */
// TODO: MYBAG-1650 refactor to not use first value
export var getJerseyIdDesignViewIds = createSelector(getJerseyIdItems, function (jerseyIdItems) {
  return jerseyIdItems.filter(function (jerseyIdItem) {
    return jerseyIdItem.valueAddedServices[0].instruction.type === JERSEY_ID;
  }).map(function (item) {
    return item.valueAddedServices[0].instruction.id;
  });
});

/**
 * Retrieves all NikeId and JerseyId desginViewIds from cart items.
 *
 * @returns {Array}
 */
export var getNikeIdAndJerseyIdDesignViewIds = createSelector(getNikeIdDesignViewIds, getJerseyIdDesignViewIds, function (nikeIdDesignViewIds, jerseyIdDesignViewIds) {
  return [].concat(_toConsumableArray(nikeIdDesignViewIds), _toConsumableArray(jerseyIdDesignViewIds));
});
/**
 * Retrieves all non-NikeID items from cart items.
 *
 * @returns {Array}
 */
export var getInlineItems = createSelector(getCartItems, function (cartItems) {
  return cartItems.filter(function (item) {
    var vas = item.valueAddedServices;
    return !vas;
  });
});

/**
 * Retrieves cart totals
 *
 * @param {object} state - The cart object
 * @returns {string}
 */
export var getCartTotals = createSelector(selectCarts, propOr('totals', TOTALS_STATE));

/**
 * Retrieves cart subtotal
 *
 * @param {object} state - The cart object
 * @returns {string}
 */
export var getCartSubtotal = createSelector(getCartTotals, propOr('subtotal', 0));

/**
 * Retrieves cart discount total
 *
 * @param {object} state - The cart object
 * @returns {string}
 */
export var getCartDiscountTotal = createSelector(getCartTotals, propOr('discountTotal', 0));

/**
 * Retrieves cart vas total
 *
 * @param {object} state - The cart object
 * @returns {string}
 */
export var getCartVASTotal = createSelector(getCartTotals, propOr('valueAddedServicesTotal', 0));

/**
 * Retrieves cart total quantity including zero
 *
 * @param {object} state - The cart object
 * @returns {string}
 */
export var getCartQuantity = createSelector(getCartTotals, propOr('quantity', 0));

/**
 * Retrieves array of services with pending http request
 *
 * @param {object} state - The cart object
 * @returns {Array}
 */
export var getInflightRequests = createSelector(selectCarts, propOr('inFlight', []));

/**
 * Retrieves the number of services with pending http request
 *
 * @param {object} state - The cart object
 * @returns {string}
 */
export var hasInflights = createSelector(getInflightRequests, function (inFlight) {
  return Boolean(inFlight.length);
});

/**
 * Retrieve cart meta
 *
 * @param {object} state - The cart object
 * @type {object}
 */
export var getCartMeta = createSelector(selectCarts, function (_ref3) {
  var meta = _ref3.meta;
  return meta || META_STATE;
});

/**
 * Retrieve appId
 *
 * @param {object} state - The cart object
 * @type {string}
 */
export var getAppId = createSelector(getCartMeta, propOr('appId', null));
export var getCartsLocaleMerchGroup = createSelector(getCartsLocale, function (_ref4) {
  var merchGroup = _ref4.merchGroup;
  return merchGroup || '';
});
export var isLoggedIn = createSelector(getCartMeta, function (meta) {
  return meta.isLoggedIn || false;
});